import { ReactNode } from 'react';
import { Field as ChakraFieldNamespace, HStack, Icon } from '@chakra-ui/react';

import { MdWarning } from '../../../icons';

export interface FieldProps extends Omit<ChakraFieldNamespace.RootProps, 'label' | 'children'> {
  label?: ReactNode;
  helperText?: ReactNode;
  errorText?: ReactNode;
  optionalText?: ReactNode;
  children?: ReactNode;
  ref?: React.RefObject<HTMLDivElement | null>;
}

export interface FieldLabelProps extends Omit<ChakraFieldNamespace.LabelProps, 'children'> {
  fallback?: React.ReactNode;
  children?: ReactNode;
  ref?: React.RefObject<HTMLLabelElement | null>;
}
export function FieldLabel({ ref, ...props }: FieldLabelProps) {
  const { fallback, ...rest } = props;
  return (
    <ChakraFieldNamespace.Label ref={ref} {...rest}>
      {props.children}
      <ChakraFieldNamespace.RequiredIndicator fallback={fallback} />
    </ChakraFieldNamespace.Label>
  );
}

export interface FieldErrorTextProps extends Omit<ChakraFieldNamespace.ErrorTextProps, 'children'> {
  children?: ReactNode;
  ref?: React.RefObject<HTMLDivElement | null>;
}
export function FieldError({ ref, ...props }: FieldErrorTextProps) {
  return (
    <ChakraFieldNamespace.ErrorText ref={ref} {...props}>
      <HStack gap={1}>
        <Icon as={MdWarning} />
        {props.children}
      </HStack>
    </ChakraFieldNamespace.ErrorText>
  );
}

export function Field({ ref, ...props }: FieldProps) {
  const { label, children, helperText, errorText, optionalText, ...rest } = props;
  return (
    <ChakraFieldNamespace.Root ref={ref} {...rest}>
      {label && (
        <FieldLabel fallback={optionalText} id={`${rest.id}-label`}>
          {label}
        </FieldLabel>
      )}
      {children}
      {helperText && (
        <ChakraFieldNamespace.HelperText id={`${rest.id}-helper-text`}>{helperText}</ChakraFieldNamespace.HelperText>
      )}
      {errorText && <FieldError id={`${rest.id}-error-text`}>{errorText}</FieldError>}
    </ChakraFieldNamespace.Root>
  );
}

export const FieldRoot = ChakraFieldNamespace.Root;
export const FieldHelperText = ChakraFieldNamespace.HelperText;
